import { useMemo, useState } from "react";
import {
  dsDnaMW,
  nucleicAcidLength,
  ssDnaMW,
  ssRnaMW,
} from "@/lib/science/nucleic-acid";
import { SciStatGrid, SciTextArea } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

type Kind = "ssDNA" | "dsDNA" | "ssRNA";
const KINDS: { value: Kind; label: () => string }[] = [
  { value: "ssDNA", label: () => m.dnamw_ssdna() },
  { value: "dsDNA", label: () => m.dnamw_dsdna() },
  { value: "ssRNA", label: () => m.dnamw_ssrna() },
];

export function DnaMwCalc() {
  const locale = getLocale();
  const [seq, setSeq] = useState("");
  const [kind, setKind] = useState<Kind>("ssDNA");

  const result = useMemo(() => {
    const mw =
      kind === "ssDNA"
        ? ssDnaMW(seq)
        : kind === "dsDNA"
          ? dsDnaMW(seq)
          : ssRnaMW(seq);
    return { mw, len: nucleicAcidLength(seq) };
  }, [seq, kind]);

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <SciTextArea
        id="dnamw-input"
        label={m.dnamw_input_label()}
        value={seq}
        placeholder={m.dnamw_input_placeholder()}
        onChange={setSeq}
        rows={3}
      />

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            aria-pressed={kind === k.value}
            onClick={() => setKind(k.value)}
            className={
              kind === k.value
                ? "sci-result-zone sci-result rounded-md px-3 py-1.5 text-sm font-semibold"
                : "sci-panel rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            }
          >
            {k.label()}
          </button>
        ))}
      </div>

      {result.len > 0 && result.mw != null ? (
        <>
          <div className="sci-result-zone mt-5 flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.dnamw_result_label()}
            </span>
            <span
              data-testid="dnamw-result"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {result.mw.toLocaleString(locale, { maximumFractionDigits: 2 })}{" "}
              <span className="text-xl">{m.dnamw_unit()}</span>
            </span>
          </div>
          <SciStatGrid
            items={[
              {
                label: m.dnamw_length_label(),
                value: result.len.toLocaleString(locale),
              },
              {
                label: m.dnamw_kda_label(),
                value: (result.mw / 1000).toLocaleString(locale, {
                  maximumFractionDigits: 2,
                }),
              },
            ]}
          />
        </>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">
          {m.dnamw_hint_empty()}
        </p>
      )}
    </div>
  );
}
